<template>
  <a class="skip-link" href="#main">Skip to content</a>
  <header class="site-header" :class="{ 'is-scrolled': scrolled }">
    <div class="header-inner">
      <router-link to="/" class="brand" aria-label="UCARI home">
        <img class="brand-logo" src="/marketing-assets/images/logo/ucari-logo-v3.png" alt="UCARI" />
      </router-link>

      <nav class="main-nav" aria-label="Primary">
        <ul>
          <template v-for="(group, index) in groups" :key="group.label">
            <li
              class="has-mega"
              :class="{ 'is-dismissed': dismissedGroup === group.label }"
              @mouseleave="restoreDesktopMenu(group.label)"
            >
              <button class="nav-link" type="button" aria-haspopup="true" @focus="restoreDesktopMenu(group.label)" @click="restoreDesktopMenu(group.label)">
                {{ group.label }}
                <svg class="marketing-chevron" viewBox="0 0 12 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="1 1 6 6 11 1" /></svg>
              </button>
              <div class="mega-menu">
                <router-link v-for="item in group.items" :key="item.to" class="mega-item" :to="item.to" @click="closeDesktopMenu(group.label, $event)">
                  <span class="mega-icon" aria-hidden="true">{{ item.icon }}</span>
                  <span><strong>{{ item.label }}</strong><span>{{ item.description }}</span></span>
                </router-link>
              </div>
            </li>
            <li v-if="index === 1"><router-link class="nav-link" to="/how-it-works">How It Works</router-link></li>
          </template>
          <li><router-link class="nav-link" to="/about">About</router-link></li>
          <li><router-link class="nav-link" to="/contact">Contact</router-link></li>
        </ul>
      </nav>

      <div class="header-actions">
        <router-link class="icon-btn marketing-account-link" to="/login" aria-label="Retailer Portal login">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        </router-link>
        <router-link class="btn btn-primary btn-sm" to="/register">Become a Retailer</router-link>
        <button class="menu-toggle" :class="{ 'is-open': mobileOpen }" type="button" aria-label="Open menu" :aria-expanded="mobileOpen" @click="mobileOpen = !mobileOpen">
          <span></span><span></span><span></span>
        </button>
      </div>
    </div>
  </header>

  <div class="mobile-nav" :class="{ 'is-open': mobileOpen }" :aria-hidden="!mobileOpen">
    <button class="backdrop" type="button" aria-label="Close menu" @click="closeMobile"></button>
    <div class="mobile-nav-panel">
      <div class="mobile-nav-head">
        <span class="brand"><img class="brand-logo" src="/marketing-assets/images/logo/ucari-logo-v3.png" alt="UCARI" /></span>
        <button class="icon-btn mobile-nav-close" type="button" aria-label="Close menu" @click="closeMobile">×</button>
      </div>
      <ul>
        <template v-for="(group, index) in groups" :key="`mobile-${group.label}`">
          <li>
            <button class="nav-link mobile-submenu-toggle" :class="{ 'is-open': openGroup === group.label }" type="button" :aria-expanded="openGroup === group.label" @click="toggleGroup(group.label)">
              {{ group.label }}
              <svg class="marketing-chevron" viewBox="0 0 12 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="1 1 6 6 11 1" /></svg>
            </button>
            <div class="mobile-submenu" :class="{ 'is-open': openGroup === group.label }">
              <router-link v-for="item in group.items" :key="item.to" :to="item.to" @click="closeMobile">{{ item.label }}</router-link>
            </div>
          </li>
          <li v-if="index === 1"><router-link class="nav-link" to="/how-it-works" @click="closeMobile">How It Works</router-link></li>
        </template>
        <li><router-link class="nav-link" to="/about" @click="closeMobile">About</router-link></li>
        <li><router-link class="nav-link" to="/contact" @click="closeMobile">Contact</router-link></li>
      </ul>
      <div class="mobile-nav-cta">
        <router-link class="btn btn-secondary btn-block" to="/login" @click="closeMobile">Retailer Portal</router-link>
        <router-link class="btn btn-primary btn-block" to="/register" @click="closeMobile">Become a Retailer</router-link>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MarketingHeader',
  data() {
    return {
      mobileOpen: false,
      openGroup: '',
      dismissedGroup: '',
      scrolled: false,
      groups: [
        {
          label: 'Products',
          items: [
            { label: 'Human Intolerance Kit', description: 'At-home sensitivity testing for people.', to: '/human-kit', icon: '◇' },
            { label: 'Pet Intolerance Kit', description: 'Sensitivity testing for dogs and cats.', to: '/pet-kit', icon: '◌' },
            { label: 'Ingredient Checker', description: 'Scan and search against a personal profile.', to: '/ingredient-checker', icon: '⌕' },
          ],
        },
        {
          label: 'For Retailers',
          items: [
            { label: 'Why UCARI', description: 'Margins, merchandising and modern tech.', to: '/why-retailers', icon: '◎' },
            { label: 'Retailer Program', description: 'Pricing, training and marketing support.', to: '/retailer-program', icon: '↗' },
            { label: 'Wholesale Pricing', description: 'Our pricing philosophy for partners.', to: '/pricing', icon: '$' },
            { label: 'Revenue Calculator', description: 'Estimate what UCARI adds to your store.', to: '/revenue-calculator#calculator', icon: '+' },
            { label: 'Success Stories', description: 'Real results from real retailers.', to: '/success-stories', icon: '★' },
            { label: 'Consumer Benefits', description: 'Why shoppers buy UCARI.', to: '/consumer-benefits', icon: '✓' },
          ],
        },
        {
          label: 'Resources',
          items: [
            { label: 'Resource Center', description: 'Guides, videos and downloads.', to: '/resources', icon: '□' },
            { label: 'Getting Started', description: 'Your simple launch plan.', to: '/getting-started', icon: '→' },
            { label: 'Marketing Toolkit', description: 'Ready-to-use retail assets.', to: '/marketing-toolkit', icon: '▧' },
            { label: 'Customer FAQ', description: 'Answers shoppers can use.', to: '/customer-faq', icon: '?' },
            { label: 'Blog', description: 'News and research from UCARI.', to: '/blog', icon: '≋' },
            { label: 'Scientific Information', description: 'Methodology and validation.', to: '/science', icon: '⌁' },
          ],
        },
      ],
    };
  },
  watch: {
    '$route.fullPath'() {
      this.closeMobile();
    },
    mobileOpen(value) {
      document.body.style.overflow = value ? 'hidden' : '';
    },
  },
  mounted() {
    this.onScroll();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('keydown', this.onKeydown);
    document.body.style.overflow = '';
  },
  methods: {
    onScroll() { this.scrolled = window.scrollY > 8; },
    onKeydown(event) { if (event.key === 'Escape') this.closeMobile(); },
    toggleGroup(label) { this.openGroup = this.openGroup === label ? '' : label; },
    closeDesktopMenu(label, event) {
      this.dismissedGroup = label;
      event.currentTarget.blur();
    },
    restoreDesktopMenu(label) {
      if (this.dismissedGroup === label) this.dismissedGroup = '';
    },
    closeMobile() { this.mobileOpen = false; this.openGroup = ''; },
  },
};
</script>
